import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, type SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';

import type {
    ObjectListConfig,
    ObjectListField,
    SettingOption,
    SettingRecord,
    SettingsContext,
    SettingsLookupId
} from '../../models/settings-hub.model';
import { getObjectListFieldError } from '../../utils/settings-hub.utils';

/** Editor of a list of objects (e.g. the fields of a form): every row shows the fields described by `ObjectListConfig` */
@Component({
    selector: 'app-settings-object-list',
    standalone: true,
    imports: [
        ActionBtnComponent,
        CdkDrag,
        CdkDragHandle,
        CdkDropList,
        FormsModule,
        MatButtonModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        TranslateModule
    ],
    templateUrl: './settings-object-list.component.html'
})
export class SettingsObjectListComponent implements OnChanges {
    @Input({ required: true }) config!: ObjectListConfig;
    @Input({ required: true }) context!: SettingsContext;
    @Input() items: SettingRecord[] = [];
    @Input() disabled = false;
    @Input() lookup?: (lookupId: SettingsLookupId) => Promise<number | undefined>;
    @Output() itemsChange = new EventEmitter<SettingRecord[]>();
    @Output() addRequest = new EventEmitter<void>();

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    protected optionsByField: Record<string, SettingOption[]> = {};
    /** The fields that the user has been in and left, as `row:key`: only their errors are shown... */
    private touched = new Set<string>();
    /** ...and all those of a row that the user has left (the focus went out of it), so a row left incomplete does not block the save without saying why */
    private touchedRows = new Set<number>();
    /** The rows as the last change of the user left them, to tell the rows that come from outside */
    private lastEmitted = '';

    ngOnChanges(changes: SimpleChanges) {
        // Rows replaced from outside (e.g. by Cancel) are not the ones that the user visited: the errors wait for the user again
        if (changes['items'] && !changes['items'].firstChange && JSON.stringify(this.items) !== this.lastEmitted) {
            this.touched.clear();
            this.touchedRows.clear();
        }

        // The options are computed again only when the fields or the loaded collections change, not at every edit
        if (changes['config'] || (changes['context'] && changes['context'].previousValue?.data !== this.context.data)) {
            this.optionsByField = Object.fromEntries(
                this.config.fields.filter(field => field.options).map(field => [field.key, field.options!(this.context)])
            );
        }
    }

    private emitItems(items: SettingRecord[]) {
        this.lastEmitted = JSON.stringify(items);
        this.itemsChange.emit(items);
    }

    protected add() {
        if (this.config.addAction) {
            this.addRequest.emit();
        } else {
            this.emitItems([...this.items, {}]);
        }
    }

    /** The error to show under a field: the user must have been in the field and left it first, so it does not complain while typing or about fields not visited */
    protected shownError(index: number, item: SettingRecord, field: ObjectListField) {
        return this.touched.has(`${index}:${field.key}`) || this.touchedRows.has(index) ? getObjectListFieldError(field, item) : null;
    }

    /**
     * When a field of the row shows an error, all the fields of the row reserve the space under them for it:
     * otherwise the one with the error would be taller than the others, and they would not line up.
     */
    protected rowHasError(index: number, item: SettingRecord) {
        return this.config.fields.some(field => this.shownError(index, item, field));
    }

    protected touchField(index: number, field: ObjectListField) {
        this.touched.add(`${index}:${field.key}`);
    }

    protected touchRowIfLeft(event: FocusEvent, index: number) {
        if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
            this.touchedRows.add(index);
        }
    }

    protected remove(index: number) {
        this.moveTouched(row => row === index ? null : row > index ? row - 1 : row);
        this.emitItems(this.items.filter((_, itemIndex) => itemIndex !== index));
    }

    /** Keeps the visited fields and rows with their rows when these change position (`null` drops them) */
    private moveTouched(newIndex: (row: number) => number | null) {
        const touched = new Set<string>();
        const touchedRows = new Set<number>();

        for (const entry of this.touched) {
            const [row, key] = [Number(entry.slice(0, entry.indexOf(':'))), entry.slice(entry.indexOf(':') + 1)];
            const moved = newIndex(row);

            if (moved !== null) {
                touched.add(`${moved}:${key}`);
            }
        }

        for (const row of this.touchedRows) {
            const moved = newIndex(row);

            if (moved !== null) {
                touchedRows.add(moved);
            }
        }

        this.touched = touched;
        this.touchedRows = touchedRows;
    }

    /** Moves the item dragged to the position where it was dropped */
    protected drop(event: CdkDragDrop<SettingRecord[]>) {
        if (event.previousIndex !== event.currentIndex) {
            this.move(event.previousIndex, event.currentIndex);
        }
    }

    /** Keyboard alternative to dragging: the arrows up and down on the handle move the item, and the handle keeps the focus */
    protected onHandleKey(event: Event, index: number, offset: -1 | 1) {
        event.preventDefault();

        if (this.disabled || index + offset < 0 || index + offset >= this.items.length) {
            return;
        }

        this.move(index, index + offset);
        setTimeout(() => this.host.nativeElement.querySelectorAll<HTMLElement>('[data-handle]')[index + offset]?.focus());
    }

    private move(from: number, to: number) {
        this.moveTouched(row => row === from ? to : from < to && row > from && row <= to ? row - 1 : from > to && row >= to && row < from ? row + 1 : row);
        const items = [...this.items];
        items.splice(to, 0, ...items.splice(from, 1));

        this.emitItems(items);
    }

    protected async pickNumber(index: number, key: string, lookupId: SettingsLookupId) {
        const value = await this.lookup?.(lookupId);

        if (value !== undefined) {
            this.setField(index, key, value);
        }
    }

    protected setField(index: number, key: string, value: string | number | boolean | null) {
        this.emitItems(this.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
    }
}
